import { ref, reactive, computed, watch } from 'vue'
import { BleClient, textToDataView } from '@capacitor-community/bluetooth-le'
import { Capacitor } from '@capacitor/core'
import {
    REINFORCE_MEMORY_PREFIX as MEMORY_PREFIX,
    makeReinforceMemoryKey as makeMemoryKey,
    stripReinforceMemoryPrefix as stripMemoryPrefix,
    readReinforceMemory as memoryGet,
    writeReinforceMemory as memorySet,
    removeReinforceMemory as memoryRemove,
    listReinforceMemoryKeys as memoryKeys,
    readCachedNumber,
    wrapCachedNumber,
} from '@/lib/sqlite/features/reinforce/memory-cache-feature'
import {
    formatDate,
    formatDuration,
    normalizeLabel,
    extractLabelFromFilename,
    formatCsv,
    REINFORCE_FORCE_MODELS,
    resolveReinforceForceModelById,
    resolveReinforceForceModelFromValues,
    computeReinforceForce,
} from './reinforce/utils'
import { createDownloadManager } from './reinforce/download'
import { createStatusManager } from './reinforce/status'

export default ({} = {}) => {
    // Firmware UUIDs
    const STREAMING_SERVICE = '19B10000-E8F2-537E-4F6C-D104768A1214'
    const COMMAND_CHAR = '19B10001-E8F2-537E-4F6C-D104768A1214'
    const DATA_CHAR = '19B10002-E8F2-537E-4F6C-D104768A1214'
    const SENSOR_VOLT_CHAR = '19B10004-E8F2-537E-4F6C-D104768A1214'
    const STATUS_CHAR = '19B10011-E8F2-537E-4F6C-D104768A1214'
    const SD_INFO_CHAR = '19B10018-E8F2-537E-4F6C-D104768A1214'
    const CLOCK_CHAR = '19B10019-E8F2-537E-4F6C-D104768A1214'
    const METRICS_CHAR = '19B1001A-E8F2-537E-4F6C-D104768A1214'
    const FILE_CHAR = '19B1001B-E8F2-537E-4F6C-D104768A1214'
    const SESSIONS_CHAR = '19B1001C-E8F2-537E-4F6C-D104768A1214'

    const BATTERY_SERVICE = '0000180f-0000-1000-8000-00805f9b34fb'
    const BATTERY_CHAR = '00002a19-0000-1000-8000-00805f9b34fb'

    const initialSensorProperties = {
        battery: 0,
        name: '',
        deviceId: '',
        tag: 'UNSET',
        latestData: null,
        latestDataDecoded: null,
        latestClock: null,
        force: '', // float as string for UI
        sensorVoltage: '', // float as string for UI
        sdFreeMB: '',
        sdTotalMB: '',
        sdFs: null, // 'exfat' | 'fat' | null
        calibrationOffset: '0.0 mV', // Calibration value
        calibrationOffsetV: 0.0, // Offset in Volts for calculations
        sdCacheReady: false,
        sdCacheAgeSec: null,
        downloadActive: null,
        downloadText: 'OFF',
        usbCharging: false,
        firmwareVersion: '',
        protocolVersion: null,
        protocolChunk: null,
        protocolCrc: '',
        protocolOk: true,
        metrics: {
            samples: 0,
            lost: 0,
            writeErr: 0,
            sdErr: 0,
            i2c: 0,
            bleDrop: 0,
            loopMax: 0,
            loopAvg: 0,
            minRam: null,
            bytesKB: 0,
        },
    }

    // Force calculation
    const PROTOCOL_VERSION = 1
    const PROTOCOL_CHUNK_SIZE = 238
    const properties = reactive({ ...initialSensorProperties, metrics: { ...initialSensorProperties.metrics } })
    const forceModelMode = ref('auto')
    const forceModelOptions = [
        { id: 'auto', label: 'Auto (from name/tag)' },
        { id: 'none', label: 'None (no force)' },
        ...REINFORCE_FORCE_MODELS.map((model) => ({
            id: model.id,
            label: model.code ? `${model.name} (${model.code})` : model.name,
        })),
    ]

    const resolveForceModel = (props) => resolveReinforceForceModelFromValues(props?.name, props?.tag, props?.deviceId)

    const getForceModelInfo = (props) => {
        const mode = forceModelMode.value || 'auto'
        if (mode === 'none') {
            return {
                mode,
                model: null,
                label: 'None',
                formula: 'No force formula applied',
                source: 'none',
            }
        }
        if (mode !== 'auto') {
            const model = resolveReinforceForceModelById(mode)
            if (!model) {
                return { mode, model: null, label: 'Unknown', formula: 'Unknown formula', source: 'manual' }
            }
            return {
                mode,
                model,
                label: model.code ? `${model.name} (${model.code})` : model.name,
                formula: `load (N) = (${model.a} * signal) + ${model.b}`,
                source: 'manual',
            }
        }

        const autoModel = resolveForceModel(props)
        if (autoModel) {
            return {
                mode,
                model: autoModel,
                label: autoModel.code ? `Auto • ${autoModel.name} (${autoModel.code})` : `Auto • ${autoModel.name}`,
                formula: `load (N) = (${autoModel.a} * signal) + ${autoModel.b}`,
                source: 'auto',
            }
        }

        return {
            mode,
            model: null,
            label: 'Auto • Unknown',
            formula: 'No matching force formula',
            source: 'auto-unmatched',
        }
    }

    const getActiveForceModel = (props) => getForceModelInfo(props).model

    const computeForceFromSignal = (signal, props) => {
        const model = getActiveForceModel(props)
        return computeReinforceForce(signal, model)
    }

    watch(
        () => forceModelMode.value,
        (mode) => {
            if (mode === 'none') {
                properties.force = ''
            }
        }
    )

    let device
    const isBusy = ref(false)
    const isConnected = ref(false)
    const isStreaming = ref(false)
    const isRecording = ref(false)
    const isTimeSynced = ref(null)
    const syncInProgress = ref(false)

    // Time sync gating: recording requires sync; streaming does not
    const canRecord = computed(
        () => isTimeSynced.value === true && isConnected.value && !isRecording.value && !isStreaming.value
    )
    const canStream = computed(() => isConnected.value && !isStreaming.value && !isRecording.value)

    const isReinforces = computed(() => true)
    const lastConnectedId = ref(undefined)
    const driver = 'reinforces'
    const PLATFORM =
        typeof Capacitor !== 'undefined' && typeof Capacitor.getPlatform === 'function'
            ? Capacitor.getPlatform()
            : 'web'

    // SD file transfer state
    const downloadingFile = ref(false)
    const downloadTotalBytes = ref(0)
    const downloadReceivedBytes = ref(0)
    const downloadFileName = ref('')
    const downloadCompletionState = ref(null) // 'success' | 'error' | null
    const downloadErrorReason = ref(null)
    // Base offset when resuming a transfer; used by UI for per-session speed
    const downloadBaseOffset = ref(0)
    const sdFiles = ref([])
    const staleCache = ref([]) // filenames present in local cache but not on device
    // Rows-per-second configuration (fallback; firmware may override)
    const ROWS_PER_SEC_DEFAULT = 100
    const rowsPerSec = ref(ROWS_PER_SEC_DEFAULT)
    const TIME_SYNC_PRE_DELAY_MS = 3000
    const TIME_SYNC_RESPONSE_TIMEOUT_MS = TIME_SYNC_PRE_DELAY_MS + 2000
    const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    const requestBleDeviceOptions = {
        namePrefix: 'rein-forces',
        optionalServices: [BATTERY_SERVICE, STREAMING_SERVICE],
    }

    async function writeCommand(cmd, delayAfterMs = 150) {
        const deviceRef = device
        if (!deviceRef?.deviceId) throw new Error('No device connected for command')
        console.log('[BLE] → CMD', cmd)

        try {
            const payload = textToDataView(cmd.endsWith('\0') ? cmd : cmd + '\0')
            await BleClient.write(deviceRef.deviceId, STREAMING_SERVICE, COMMAND_CHAR, payload)
            console.log('[BLE] ✔ CMD sent')

            if (delayAfterMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, delayAfterMs))
            }
        } catch (error) {
            console.error('[BLE] Command write error:', error)
            throw error
        }
    }

    const textDecoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null

    const decodeBleString = (dataView) => {
        if (!dataView || typeof dataView.byteLength !== 'number') return ''
        try {
            const buffer = dataView.buffer
            const offset = dataView.byteOffset || 0
            const length = dataView.byteLength || 0
            if (!buffer || length === 0) return ''
            const bytes = new Uint8Array(buffer, offset, length)
            let end = bytes.length
            for (let i = 0; i < bytes.length; i += 1) {
                if (bytes[i] === 0) {
                    end = i
                    break
                }
            }
            const slice = bytes.subarray(0, end)
            if (!slice.length) return ''
            if (textDecoder) return textDecoder.decode(slice)
            let text = ''
            for (let i = 0; i < slice.length; i += 1) {
                text += String.fromCharCode(slice[i])
            }
            return text
        } catch (decodeError) {
            console.warn('[BLE] Failed to decode BLE string', decodeError)
            return ''
        }
    }

    // Download stats for UI (reactive)
    let lastDevMs = null
    let _devmsWaiter = null

    const {
        downloadStats,
        downloadMissing,
        collectingSessions,
        sessionsBuffer,
        sessionsExpectedCount,
        sessionsReceivedCount,
        sessionsRequestId,
        sessionsCompletedId,
        sessionsRetryCount,
        resetDownloadStatsState,
        clearEtaState,
        handleFtLine,
        handleFileChunkDv,
        handleDeleteStatus,
        downloadFileFromSD,
        downloadFileContentFromSD,
        getResumeInfo,
        getCachedFileText,
        deleteFileFromSD,
        listSessions,
        fetchSizeForFile,
        listDownloadCacheKeys,
        clearDownloadCache,
        clearDownloadCacheFor,
        scanStaleDownloadCache,
        pauseDownload,
        resetDownloadControllerState,
        listAndGetSDFiles,
        handleFtError,
        markSessionsCompleted,
        clearSessionsTimeout,
        resumePendingDownload,
        recoveringChunks,
        resumeReload,
        resumePosToken,
        chunkCount,
        persistedContiguous,
        crcErrorCount,
        downloadReadyUrl,
        downloadReadyName,
        downloadReadyAt,
        clearDownloadReady,
    } = createDownloadManager({
        BleClient,
        textToDataView,
        constants: { STREAMING_SERVICE, COMMAND_CHAR, FILE_CHAR },
        state: {
            properties,
            downloadingFile,
            downloadTotalBytes,
            downloadReceivedBytes,
            downloadFileName,
            downloadCompletionState,
            downloadErrorReason,
            downloadBaseOffset,
            rowsPerSec,
            sdFiles,
            staleCache,
        },
        memory: { memoryKeys, memoryGet, memorySet, memoryRemove },
        helpers: {
            formatDate,
            formatDuration,
            normalizeLabel,
            extractLabelFromFilename,
            formatCsv,
            readCachedNumber,
            wrapCachedNumber,
        },
        forceModel: () => getActiveForceModel(properties),
        getDevice: () => device,
        writeCommand,
    })

    // Parse du status (caractéristique String)
    const { parseStatusDataview } = createStatusManager({
        state: { properties, isTimeSynced, isRecording, isStreaming },
        download: {
            handleFtLine,
            handleFtError,
        },
        deleteOps: { handleDeleteStatus },
        helpers: { normalizeLabel, extractLabelFromFilename, formatDate, formatDuration },
        memory: { memoryGet },
        downloads: {
            downloadTotalBytes,
            downloadFileName,
            downloadStats,
            recoveringChunks,
            rowsPerSec,
            downloadingFile,
            sdFiles,
            persistedContiguous,
        },
        protocol: {
            version: PROTOCOL_VERSION,
            chunkSize: PROTOCOL_CHUNK_SIZE,
            crc: 'ccitt',
        },
        timeSync: {
            onDevms: ({ devms, t2Epoch, p2 }) => {
                lastDevMs = devms
                if (_devmsWaiter) {
                    const cb = _devmsWaiter
                    _devmsWaiter = null
                    try {
                        cb({ devms, t2Epoch, p2 })
                    } catch {}
                }
            },
        },
    })

    const calibrate = async () => {
        console.log('[BLE] Calibrate requested')
        try {
            await writeCommand('calibrate')
            console.log('[BLE] Calibrate sent')
            return true
        } catch (e) {
            console.error('[BLE] Calibrate failed:', e)
            return false
        }
    }

    const syncTime = async () => {
        if (syncInProgress.value) {
            console.warn('[BLE] Time sync already in progress')
            return false
        }

        const previousBusy = isBusy.value
        const resumeAfter = { download: false }

        syncInProgress.value = true
        isBusy.value = true

        try {
            if (downloadingFile.value) {
                try {
                    const paused = await pauseDownload()
                    resumeAfter.download = paused
                    if (paused) {
                        console.log('[Download] Paused active transfer before time sync')
                    }
                } catch (pauseError) {
                    console.warn('[Download] Failed to pause before sync', pauseError)
                }

                // Give firmware a brief window to settle transfer state
                await waitMs(500)
            }

            console.log(`[BLE] Preparing time sync guard (${TIME_SYNC_PRE_DELAY_MS}ms)`)
            await waitMs(TIME_SYNC_PRE_DELAY_MS)

            isTimeSynced.value = null
            _devmsWaiter = null
            try {
                properties.latestClock = null
            } catch {}

            const hasPerf = typeof performance !== 'undefined' && typeof performance.now === 'function'
            const perfOrigin = hasPerf && typeof performance.timeOrigin === 'number' ? performance.timeOrigin : null

            const waitDevms = (timeoutMs = TIME_SYNC_RESPONSE_TIMEOUT_MS) =>
                new Promise((resolve, reject) => {
                    let done = false
                    const timer = setTimeout(() => {
                        if (done) return
                        done = true
                        _devmsWaiter = null
                        reject(new Error('timeout'))
                    }, timeoutMs)
                    _devmsWaiter = (info) => {
                        if (done) return
                        done = true
                        _devmsWaiter = null
                        try {
                            clearTimeout(timer)
                        } catch {}
                        resolve(info)
                    }
                })

            try {
                const quickEpoch = Date.now()
                await writeCommand(`tsync,${quickEpoch}`, 0)
                isTimeSynced.value = true
                try {
                    properties.latestClock = quickEpoch
                } catch {}
            } catch (e) {
                console.warn('[BLE] Quick tsync failed (continuing with ts2):', e)
            }

            const samples = []
            const TARGET_SAMPLES = 4
            const MAX_ATTEMPTS = 6
            for (let attempt = 0; attempt < MAX_ATTEMPTS && samples.length < TARGET_SAMPLES; attempt++) {
                lastDevMs = null
                const startEpoch = Date.now()
                const startPerf = hasPerf ? performance.now() : null
                const waiter = waitDevms(TIME_SYNC_RESPONSE_TIMEOUT_MS)
                await writeCommand('ts2?', 0)
                try {
                    const info = await waiter
                    const endEpoch = info?.t2Epoch ?? Date.now()
                    const endPerf = info?.p2 ?? null

                    let rtt = endEpoch - startEpoch
                    if (startPerf != null && endPerf != null) {
                        rtt = endPerf - startPerf
                    }
                    if (!Number.isFinite(rtt) || rtt < 0) continue

                    let epochMid = startEpoch + rtt / 2
                    if (startPerf != null && endPerf != null) {
                        const anchor = perfOrigin != null ? perfOrigin : startEpoch
                        epochMid = anchor + (startPerf + endPerf) / 2
                    }

                    const devms = Number(info?.devms ?? lastDevMs)
                    if (!Number.isFinite(devms)) continue

                    samples.push({ epochMid: Math.round(epochMid), devms: Math.round(devms), rtt })
                    if (samples.length < TARGET_SAMPLES) {
                        await waitMs(20)
                    }
                } catch (err) {
                    // ignore individual failures and continue attempts
                    await waitMs(200)
                }
            }

            _devmsWaiter = null
            if (samples.length > 0) {
                samples.sort((a, b) => a.rtt - b.rtt)
                const best = samples[0]
                const finalEpoch = Math.round(best.epochMid)
                const finalDevms = Math.round(best.devms)
                console.log(
                    '[BLE] Time sync (ts2) best → epochMid=',
                    finalEpoch,
                    ' devms=',
                    finalDevms,
                    ' rtt=',
                    Number.isFinite(best.rtt) ? best.rtt.toFixed(2) : best.rtt
                )
                await writeCommand(`ts2,${finalEpoch},${finalDevms}`, 0)
                try {
                    properties.latestClock = finalEpoch
                } catch {}
                isTimeSynced.value = true
            } else {
                const epochMs = Date.now()
                console.warn('[BLE] Time sync (ts2) fallback →', epochMs)
                await writeCommand(`tsync,${epochMs}`, 0)
                try {
                    properties.latestClock = epochMs
                } catch {}
                isTimeSynced.value = true
            }

            if (isTimeSynced.value === true) {
                try {
                    properties.latestClock = Date.now()
                } catch {}
                console.log('[BLE] ✅ Time sync confirmed')
                lastDevMs = null
                return true
            }

            console.warn('[BLE] ⚠️ Time sync timeout')
            isTimeSynced.value = false
            lastDevMs = null
            return false
        } catch (e) {
            console.error('[BLE] Time sync failed:', e)
            isTimeSynced.value = false
            lastDevMs = null
            return false
        } finally {
            _devmsWaiter = null
            if (resumeAfter.download) {
                try {
                    await resumePendingDownload()
                    console.log('[Download] Resumed transfer after time sync')
                } catch (resumeError) {
                    console.warn('[Download] Resume after sync failed', resumeError)
                }
            }
            syncInProgress.value = false
            isBusy.value = previousBusy
        }
    }

    const getStatus = async () => {
        try {
            console.log('[BLE] Status?')
            await writeCommand('status?')
            return true
        } catch (e) {
            console.error('[BLE] Status failed:', e)
            return false
        }
    }

    let packetsCount = 0
    let firstPktTs = 0
    let lastPktTs = 0
    const resetDataStats = () => {
        packetsCount = 0
        firstPktTs = 0
        lastPktTs = 0
    }

    // Parse du DataPacket du firmware (16 bytes: 8 timestamp + 4 force(unused) + 4 voltage)
    const parseDataPacket = (dv) => {
        if (dv.byteLength < 16) {
            console.warn('[BLE] DATA packet too small:', dv.byteLength, 'expected 16')
            return null
        }

        // uint64_t timestamp (little endian)
        const lo = dv.getUint32(0, true)
        const hi = dv.getUint32(4, true)
        const ts = hi * 2 ** 32 + lo

        // float force (ignorée - firmware envoie 0, on calcule localement)
        // dv.getFloat32(8, true) - non utilisé

        // float voltage (little endian)
        const voltage = dv.getFloat32(12, true)

        // Calcul force via formule capteur
        const force = computeForceFromSignal(voltage, properties)

        // Ajouter info sur le sync status au timestamp
        const syncInfo = isTimeSynced.value === true ? 'synced' : isTimeSynced.value === false ? 'relative' : 'unknown'

        return {
            timestamp: ts,
            force,
            voltage,
            timeSync: syncInfo,
            epochMs: isTimeSynced.value === true ? ts : null,
        }
    }

    // Helpers d'export retirés (plus de buffer RAM)

    const connect = async () => {
        console.log('[BLE] Starting connection…')

        try {
            await BleClient.stopLEScan()
        } catch {}

        isBusy.value = true
        try {
            const dev = await BleClient.requestDevice(requestBleDeviceOptions)
            if (!dev) throw new Error('No device selected')
            await connectDevice(dev)
        } catch (e) {
            console.error('[BLE] connect error:', e)
            await disconnect()
        } finally {
            isBusy.value = false
            console.log('[BLE] Connection done. isConnected=', isConnected.value)
        }
        return isConnected.value
    }

    const onDisconnect = async (deviceId) => {
        console.warn('[BLE] Disconnected by remote:', deviceId)
        await disconnect()
    }

    const connectDevice = async (newDevice) => {
        if (isConnected.value && lastConnectedId.value === newDevice.deviceId) {
            console.warn('[BLE] Already connected, skip.')
            return
        }

        isBusy.value = true
        device = newDevice
        const devId = newDevice.deviceId

        try {
            try {
                await resetDownloadControllerState('pre-connect')
            } catch {}
            if (lastConnectedId.value !== device.deviceId) {
                lastConnectedId.value = device.deviceId
                memorySet('lastConnectedReinforcesId', device.deviceId).catch(() => {})
            }

            properties.name = device.name || ''
            properties.deviceId = device.deviceId || ''
            console.log('[BLE] Connecting to', properties.name, device.deviceId)

            // Éviter un disconnect préventif côté WebBLE qui peut casser l'état.
            await BleClient.connect(devId, onDisconnect)
            console.log('[BLE] ✔ Connected')
            isConnected.value = true

            const ensureConnected = () => isConnected.value && device?.deviceId === devId
            // Laisser 100ms pour que les services soient prêts sur certaines piles WebBLE
            await new Promise((r) => setTimeout(r, 100))

            // Send current label to device so firmware can tag filenames/status
            try {
                const label = normalizeLabel(properties.tag)
                if (label && label !== 'UNSET') {
                    if (!ensureConnected()) throw new Error('disconnected')
                    properties.tag = label
                    await writeCommand(`tag=${label}`)
                    try {
                        await writeCommand('status?')
                    } catch {}
                    console.log('[BLE] ✔ Tag sent to device:', label)
                }
            } catch (e) {
                console.warn('[BLE] Tag set failed/ignored:', e?.message || e)
            }

            // Lire la batterie
            try {
                console.log('[BLE] Battery asked')
                if (!ensureConnected()) throw new Error('disconnected')
                const bat = await BleClient.read(devId, BATTERY_SERVICE, BATTERY_CHAR)
                properties.battery = bat.getUint8(0)
                console.log('[BLE] Battery initial:', properties.battery, '%')
            } catch (e) {
                console.warn('[BLE] Battery read failed:', e)
            }

            // Notifications batterie
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, BATTERY_SERVICE, BATTERY_CHAR, (dv) => {
                    properties.battery = dv.getUint8(0)
                    console.log('[BLE] Battery notify:', properties.battery, '%')
                })
            } catch (e) {
                console.warn('[BLE] Battery notify failed:', e)
            }

            // Lire tension capteur (READ) et calculer force côté front
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                const dvV = await BleClient.read(devId, STREAMING_SERVICE, SENSOR_VOLT_CHAR)
                const v = dvV.getFloat32(0, true)
                properties.sensorVoltage = String(v.toFixed(4))
                // Calcul initial de la force: formule capteur
                const force = computeForceFromSignal(v, properties)
                if (force !== null) {
                    properties.force = String(force.toFixed(3))
                } else {
                    properties.force = ''
                }

                console.log(
                    '[BLE] SensorVoltage initial:',
                    properties.sensorVoltage,
                    'V → Force:',
                    properties.force,
                    'N'
                )
            } catch (e) {
                console.warn('[BLE] SensorVoltage read failed:', e)
            }

            // Notifications tension capteur (force recalculée côté front)
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, SENSOR_VOLT_CHAR, (dv) => {
                    const v = dv.getFloat32(0, true)
                    properties.sensorVoltage = String(v.toFixed(4))

                    // Calculer la force à partir du voltage et de la formule capteur
                    const force = computeForceFromSignal(v, properties)
                    if (force !== null) {
                        properties.force = String(force.toFixed(3))
                    } else {
                        properties.force = ''
                    }

                    console.log(
                        '[BLE] SensorVoltage notify:',
                        properties.sensorVoltage,
                        'V → Force:',
                        properties.force,
                        'N'
                    )
                })
            } catch (e) {
                console.warn('[BLE] SensorVoltage notify failed:', e)
            }

            // Notifications status
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, STATUS_CHAR, async (dv) => {
                    await parseStatusDataview(dv)
                })
                console.log('[BLE] Status notifications started')
                // Seed initial status so UI gets time sync/protocol state immediately
                try {
                    await writeCommand('status?')
                    await writeCommand('ver?')
                } catch (e) {
                    console.warn('[BLE] Seed status failed:', e)
                }
            } catch (e) {
                console.warn('[BLE] Status notify failed:', e)
            }

            // Notifications binary file transfer
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, FILE_CHAR, (dv) => {
                    handleFileChunkDv(dv)
                })
                console.log('[BLE] FILE notifications started')
            } catch (e) {
                console.warn('[BLE] FILE notify failed:', e)
            }

            // Sessions listing notifications (separate characteristic)
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, SESSIONS_CHAR, async (dv) => {
                    console.log('[BLE] Sessions notification received start')
                    const payload = decodeBleString(dv)
                    if (!payload) return
                    const lines = payload.split(/\r?\n/)
                    for (const rawLine of lines) {
                        const line = rawLine.trim()
                        if (!line) continue
                        console.log('[BLE] Sessions notification received:', line)
                        if (handleFtLine) {
                            try {
                                await handleFtLine(line)
                            } catch (err) {
                                console.warn('[BLE] Sessions line handling error:', err)
                            }
                        }
                    }
                })
                console.log('[BLE] Sessions notifications started')
                // Listing SD files should be user-triggered (avoid blocking connect)
            } catch (e) {
                console.warn('[BLE] Sessions notify failed:', e)
            }

            // Notifications SD info (consolidated)
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, SD_INFO_CHAR, (dv) => {
                    // Decode text
                    let text = ''
                    for (let i = 0; i < dv.byteLength; i++) {
                        const c = dv.getUint8(i)
                        if (c === 0) break
                        text += String.fromCharCode(c)
                    }
                    if (!text) return

                    // Log similaire au STATUS mais pour la SD (verbose only)
                    console.log('[BLE] ← SD', text)

                    // sdFs (format) peut être publié par la caractéristique SD
                    const mFs = text.match(/sdFs=(\w+)/i)
                    if (mFs) properties.sdFs = mFs[1].toLowerCase()

                    // sd readiness / cache info
                    const mReady = text.match(/sdReady=(\d)/)
                    if (mReady) properties.sdCacheReady = mReady[1] === '1'
                    const mAge = text.match(/sdAge=(\d+)/)
                    if (mAge) properties.sdCacheAgeSec = parseInt(mAge[1])

                    // Heartbeat-like counters
                    const mFree = text.match(/sdFree=(\d+)/)
                    if (mFree) properties.sdFreeMB = mFree[1]
                    const mTot = text.match(/sdTotal=(\d+)/)
                    if (mTot) properties.sdTotalMB = mTot[1]

                    // Réponses explicites à sd? peuvent aussi arriver ici: free=XXXXMB,total=YYYYMB
                    const sFree = text.match(/free=(\d+)MB/)
                    if (sFree) properties.sdFreeMB = sFree[1]
                    const sTot = text.match(/total=(\d+)MB/)
                    if (sTot) properties.sdTotalMB = sTot[1]
                })
                console.log('[BLE] SD info notifications started')
                // Seed initial SD info (free/total) in case the device does not push immediately
                try {
                    await writeCommand('sd?')
                } catch (e) {
                    console.warn('[BLE] Seed SD info failed:', e)
                }
            } catch (e) {
                console.warn('[BLE] SD info notify failed:', e)
            }

            // Notifications clock
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, CLOCK_CHAR, (dv) => {
                    if (dv.byteLength >= 8) {
                        const lo = dv.getUint32(0, true)
                        const hi = dv.getUint32(4, true)
                        const ts = hi * 2 ** 32 + lo
                        // Optionally expose device clock
                        properties.latestClock = ts
                    }
                })
                console.log('[BLE] Clock notifications started')
            } catch (e) {
                console.warn('[BLE] Clock notify failed:', e)
            }

            // Notifications metrics/diagnostics
            try {
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, METRICS_CHAR, (dv) => {
                    let text = ''
                    for (let i = 0; i < dv.byteLength; i++) {
                        const c = dv.getUint8(i)
                        if (c === 0) break
                        text += String.fromCharCode(c)
                    }
                    if (!text) return
                    // console.log('[BLE] ← METRICS', text)

                    const parseNum = (re) => {
                        const m = text.match(re)
                        return m ? parseInt(m[1]) : undefined
                    }
                    const m = properties.metrics
                    const v = {
                        samples: parseNum(/samples=(\d+)/),
                        lost: parseNum(/lost=(\d+)/),
                        writeErr: parseNum(/wErr=(\d+)/),
                        sdErr: parseNum(/sdErr=(\d+)/),
                        i2c: parseNum(/i2c=(\d+)/),
                        bleDrop: parseNum(/ble=(\d+)/),
                        loopMax: parseNum(/lMax=(\d+)/),
                        loopAvg: parseNum(/lAvg=(\d+)/),
                        minRam: parseNum(/minRam=(\d+)/),
                        bytesKB: parseNum(/bytesKB=(\d+)/),
                        timeErr: parseNum(/tErr=(\d+)/),
                        recS: parseNum(/recS=(\d+)/),
                        bleS: parseNum(/bleS=(\d+)/),
                        upS: parseNum(/upS=(\d+)/),
                    }
                    Object.keys(v).forEach((k) => {
                        if (typeof v[k] === 'number' && !Number.isNaN(v[k])) m[k] = v[k]
                    })
                })
                console.log('[BLE] Metrics notifications started')
            } catch (e) {
                console.warn('[BLE] Metrics notify failed:', e)
            }

            // Notifications data
            try {
                resetDataStats()
                if (!ensureConnected()) throw new Error('disconnected')
                await BleClient.startNotifications(devId, STREAMING_SERVICE, DATA_CHAR, (dv) => {
                    const pkt = parseDataPacket(dv)
                    if (!pkt) return

                    packetsCount += 1
                    if (!firstPktTs) firstPktTs = performance.now()
                    lastPktTs = performance.now()

                    if (packetsCount % 10 === 0) {
                        const elapsed = (lastPktTs - firstPktTs) / 1000
                        const rate = elapsed > 0 ? (packetsCount / elapsed).toFixed(2) : '∞'
                        console.log(`[BLE] DATA packets: ${packetsCount} (${rate} Hz)`)
                    }

                    properties.latestData = new Uint8Array(dv.buffer.slice(0, dv.byteLength))
                    properties.latestDataDecoded = pkt // Garder les infos courantes pour l'UI
                })
                console.log('[BLE] Data notifications started')
            } catch (e) {
                console.warn('[BLE] Data notify failed:', e)
            }

            // Pas de commandes "status?"/"sd?" automatiques: on se repose sur les notifications et seeds
        } catch (e) {
            console.error('[BLE] connectDevice error:', e)
            await disconnect()
        } finally {
            isBusy.value = false
        }
    }

    // Reconnect directly by deviceId saved in Memory (if OS/stack allows)
    const reconnectById = async (deviceId) => {
        try {
            if (!deviceId) return false
            const dev = { deviceId, name: '' }
            await connectDevice(dev)
            return isConnected.value
        } catch (e) {
            console.warn('[BLE] reconnectById failed:', e)
            return false
        }
    }

    const disconnect = async (forgetDevice = false) => {
        console.log('[BLE] Disconnecting… forget=', forgetDevice)
        isBusy.value = true

        try {
            if (forgetDevice) {
                await memoryRemove('lastConnectedReinforcesId')
                lastConnectedId.value = undefined
                console.log('[BLE] Forgot saved device id')
            }

            if (device?.deviceId) {
                try {
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, DATA_CHAR)
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, STATUS_CHAR)
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, FILE_CHAR)
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, SESSIONS_CHAR)
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, SENSOR_VOLT_CHAR)
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, SD_INFO_CHAR)
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, CLOCK_CHAR)
                    await BleClient.stopNotifications(device.deviceId, STREAMING_SERVICE, METRICS_CHAR)
                    await BleClient.stopNotifications(device.deviceId, BATTERY_SERVICE, BATTERY_CHAR)
                    console.log('[BLE] Stopped notifications')
                } catch (e) {
                    console.warn('[BLE] stopNotifications error:', e)
                }

                try {
                    await BleClient.disconnect(device.deviceId)
                    console.log('[BLE] BLE disconnected')
                } catch (e) {
                    console.warn('[BLE] disconnect error:', e)
                }
            }
        } catch (e) {
            console.error('[BLE] disconnect flow error:', e)
        } finally {
            Object.assign(properties, { ...initialSensorProperties, metrics: { ...initialSensorProperties.metrics } })
            device = undefined
            isConnected.value = false
            isStreaming.value = false
            isRecording.value = false
            isTimeSynced.value = null
            packetsCount = 0
            firstPktTs = 0
            lastPktTs = 0
            isBusy.value = false
            // Persist resume point if we had a partial download
            try {
                const resume = downloadBaseOffset.value + downloadReceivedBytes.value
                if (
                    downloadFileName.value &&
                    resume > 0 &&
                    (downloadTotalBytes.value === 0 || resume < downloadTotalBytes.value)
                ) {
                    await memorySet(`resume:${downloadFileName.value}`, wrapCachedNumber(resume))
                }
            } catch {}
            // Reset any ongoing download state for UI
            try {
                await resetDownloadControllerState('disconnect')
            } catch {}
            console.log('[BLE] Disconnected. State cleared.')
        }
    }

    const startStreaming = async () => {
        if (!device) {
            console.warn('[BLE] startStreaming without device')
            return { success: false, error: 'no-device' }
        }

        if (isRecording.value) {
            console.warn('[BLE] startStreaming blocked: recording active')
            return { success: false, error: 'recording-active' }
        }

        // Streaming autorisé même sans time sync (juste un warning)
        if (isTimeSynced.value === false) {
            console.warn('[BLE] ⚠️ Streaming without time sync - timestamps will be relative')
        } else if (isTimeSynced.value == null) {
            console.warn('[BLE] ⚠️ Streaming with unknown time sync')
        }

        try {
            await writeCommand('stream=on')
            // L'état sera mis à jour par la notification status
            console.log('[BLE] Stream command sent')
            return { success: true }
        } catch (e) {
            console.error('[BLE] startStreaming failed:', e)
            return { success: false, error: 'ble-error' }
        }
    }

    const startRecording = async () => {
        if (!device) {
            console.warn('[BLE] startRecording without device')
            return { success: false, error: 'no-device' }
        }

        if (isStreaming.value) {
            console.warn('[BLE] startRecording blocked: streaming active')
            return { success: false, error: 'streaming-active' }
        }

        // Validation time sync
        if (isTimeSynced.value !== true) {
            console.warn('[BLE] startRecording without time sync')
            return { success: false, error: 'time-not-synced' }
        }

        try {
            // reset stats uniquement
            packetsCount = 0
            firstPktTs = 0
            lastPktTs = 0

            // Démarrer l'enregistrement uniquement (streaming géré séparément)
            await writeCommand('start', 0)

            // Attendre confirmation "recording=on" (ou timeout)
            const startWait = Date.now()
            const timeoutMs = 6000
            while (!isRecording.value && Date.now() - startWait < timeoutMs) {
                await new Promise((r) => setTimeout(r, 50))
            }

            if (!isRecording.value) {
                console.warn('[BLE] startRecording: device did not confirm recording ON')
                return { success: false, error: 'device-rejected' }
            }

            console.log('[BLE] Recording confirmed ON')
            return { success: true }
        } catch (e) {
            console.error('[BLE] startRecording failed:', e)
            return { success: false, error: 'ble-error' }
        }
    }

    const stopRecording = async () => {
        if (!device) {
            console.warn('[BLE] stopRecording without device')
            return false
        }
        try {
            // Arrêter l'enregistrement sur la SD
            await writeCommand('stop')

            // Optionnel: arrêter aussi le streaming si on veut arrêter tout
            // Pour l'instant on laisse le streaming actif pour permettre la visualisation
            // await writeCommand('stream=off')

            const elapsed = (lastPktTs - firstPktTs) / 1000
            const rate = elapsed > 0 ? (packetsCount / elapsed).toFixed(2) : '0'
            console.log(`[BLE] Recording STOP. Captured ${packetsCount} pkts in ${elapsed.toFixed(2)}s (${rate} Hz)`)
            return true
        } catch (e) {
            console.error('[BLE] stopRecording failed:', e)
            return false
        }
    }

    const stopStreaming = async () => {
        if (!device) {
            console.warn('[BLE] stopStreaming without device')
            return false
        }
        try {
            await writeCommand('stream=off')
            console.log('[BLE] Streaming stopped')
            return true
        } catch (e) {
            console.error('[BLE] stopStreaming failed:', e)
            return false
        }
    }

    // Fonctions RAM retirées (removeExercise/getExercise/saveToMemory)

    // Dynamically set tag (LEFT/RIGHT or custom) and send to device if connected
    const setTag = async (tagText) => {
        try {
            const normalized = normalizeLabel(tagText)
            if (!normalized) {
                properties.tag = 'UNSET'
                return false
            }
            properties.tag = normalized
            if (device?.deviceId) {
                await writeCommand(`tag=${normalized}`)
                try {
                    await writeCommand('status?')
                } catch {}
            }
            return true
        } catch (e) {
            console.warn('[BLE] setTag error', e)
            return false
        }
    }

    return {
        properties,
        driver,
        isBusy,
        isReinforces,
        isRecording,
        isStreaming,
        isTimeSynced,
        syncInProgress,
        canRecord,
        canStream,
        isConnected,
        connect,
        connectDevice,
        disconnect,
        lastConnectedId,
        startStreaming,
        startRecording,
        stopRecording,
        stopStreaming,
        calibrate,
        syncTime,
        getStatus,
        listSessions,
        collectingSessions,
        sessionsBuffer,
        sessionsExpectedCount,
        sessionsReceivedCount,
        sessionsRequestId,
        sessionsCompletedId,
        sessionsRetryCount,
        writeCommand,
        downloadFileFromSD,
        deleteFileFromSD,
        listAndGetSDFiles,
        listDownloadCacheKeys,
        clearDownloadCache,
        clearDownloadCacheFor,
        scanStaleDownloadCache,
        getResumeInfo,
        staleCache,
        sdFiles,
        downloadingFile,
        downloadFileName,
        downloadTotalBytes,
        downloadReceivedBytes,
        rowsPerSec,
        downloadStats,
        downloadMissing,
        downloadBaseOffset,
        recoveringChunks,
        resumeReload,
        resumePosToken,
        chunkCount,
        persistedContiguous,
        crcErrorCount,
        downloadReadyUrl,
        downloadReadyName,
        downloadReadyAt,
        getCachedFileText,
        pauseDownload,
        downloadCompletionState,
        downloadErrorReason,
        downloadFileContentFromSD,
        clearDownloadReady,
        setTag,
        reconnectById,
        forceModelMode,
        forceModelOptions,
        getForceModelInfo,
        getActiveForceModel,
        protocolExpected: {
            version: PROTOCOL_VERSION,
            chunk: PROTOCOL_CHUNK_SIZE,
            crc: 'ccitt',
        },
        memoryHelpers: {
            MEMORY_PREFIX,
            makeMemoryKey,
            stripMemoryPrefix,
            memoryGet,
            memorySet,
            memoryRemove,
            memoryKeys,
        },
    }
}
